import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Howler } from 'howler';
import useSound from 'use-sound';
import { useUserStore } from '../store/userStore';

export type BgmTheme = 'lobby' | 'daily' | 'sunday';

interface AudioContextType {
    setBgmTheme: (theme: BgmTheme) => void;
    playClick: () => void;
    playPopBalls: () => void;
    playWinSmallPrize: () => void;
    playPopLoss: () => void;
    playBingoWin: () => void;
    playClaim: () => void;
    // Legacy aliases to prevent breaking existing code during migration
    playPop: () => void;
    playFanfare: () => void;
}

export const AudioContext = React.createContext<AudioContextType>({
    setBgmTheme: () => { },
    playClick: () => { },
    playPopBalls: () => { },
    playWinSmallPrize: () => { },
    playPopLoss: () => { },
    playBingoWin: () => { },
    playClaim: () => { },
    playPop: () => { },
    playFanfare: () => { }
});

export const useAudio = () => React.useContext(AudioContext);

export function AudioProvider({ children }: { children: React.ReactNode }) {
    const { settings } = useUserStore();
    const [hasInteracted, setHasInteracted] = useState(false);
    const [theme, setTheme] = useState<BgmTheme>('lobby');
    const bgmThemeRef = useRef<BgmTheme>('lobby');

    // Make sure we keep the ref synced for the effect
    useEffect(() => {
        bgmThemeRef.current = theme;
    }, [theme]);

    // Background Musics
    const [playLobby, { pause: pauseLobby, sound: lobbySound }] = useSound('/audio/lobby-bgm.mp3', { loop: true, volume: 0.15 });
    const [playDaily, { pause: pauseDaily, sound: dailySound }] = useSound('/audio/daily-bgm.mp3', { loop: true, volume: 0.15 });
    const [playSunday, { pause: pauseSunday, sound: sundaySound }] = useSound('/audio/sunday-bgm.mp3', { loop: true, volume: 0.20 });

    // Sound Effects
    const [playClickSfx] = useSound('/audio/click.mp3', { volume: 0.8 });
    const [playPopBallsSfx] = useSound('/audio/Pop-balls.mp3', { volume: 1.0 });
    const [playWinSmallPrizeSfx] = useSound('/audio/win-small-prize.mp3', { volume: 1.0 });
    const [playPopLossSfx] = useSound('/audio/Pop-loss.mp3', { volume: 0.8 });
    const [playBingoWinSfx] = useSound('/audio/Bingo-win.mp3', { volume: 1.0 });
    const [playClaimSfx] = useSound('/audio/reclamas-un-carton.mp3', { volume: 1.0 });

    // Global pause on visibility loss (mobile game dev best practice)
    useEffect(() => {
        const handleFocus = () => {
            if (settings.sound) {
                Howler.mute(false);
            }
        };

        const handleBlur = () => {
            Howler.mute(true);
        };

        const handleVisibilityChange = () => {
            if (document.hidden) {
                Howler.mute(true);
            } else if (settings.sound) {
                Howler.mute(false);
            }
        };

        window.addEventListener('focus', handleFocus);
        window.addEventListener('blur', handleBlur);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            window.removeEventListener('focus', handleFocus);
            window.removeEventListener('blur', handleBlur);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [settings.sound]);

    // Apply global mute when settings change
    useEffect(() => {
        Howler.mute(!settings.sound);
    }, [settings.sound]);

    // Handle BGM playback logic
    const updateBgmPlayback = useCallback(() => {
        if (!hasInteracted) return;

        // First, stop all tracks
        if (lobbySound) pauseLobby();
        if (dailySound) pauseDaily();
        if (sundaySound) pauseSunday();

        // If sound is enabled, play the correct track
        if (settings.sound) {
            if (bgmThemeRef.current === 'lobby' && lobbySound && !lobbySound.playing()) {
                playLobby();
            } else if (bgmThemeRef.current === 'daily' && dailySound && !dailySound.playing()) {
                playDaily();
            } else if (bgmThemeRef.current === 'sunday' && sundaySound && !sundaySound.playing()) {
                playSunday();
            }
        }
    }, [hasInteracted, settings.sound, lobbySound, dailySound, sundaySound, playLobby, playDaily, playSunday, pauseLobby, pauseDaily, pauseSunday]);

    // React to theme or settings changes
    useEffect(() => {
        updateBgmPlayback();
    }, [theme, settings.sound, hasInteracted, updateBgmPlayback]);

    // Handle first interaction to start BGM
    useEffect(() => {
        if (hasInteracted) return;

        const onInteract = () => {
            setHasInteracted(true);
            // Remove listeners after first interaction
            document.removeEventListener('click', onInteract);
            document.removeEventListener('touchstart', onInteract);
            document.removeEventListener('keydown', onInteract);
        };

        // Added keydown just in case spacebar/enter is used
        document.addEventListener('click', onInteract);
        document.addEventListener('touchstart', onInteract);
        document.addEventListener('keydown', onInteract);

        return () => {
            document.removeEventListener('click', onInteract);
            document.removeEventListener('touchstart', onInteract);
            document.removeEventListener('keydown', onInteract);
        };
    }, [hasInteracted]);

    const contextValue: AudioContextType = {
        setBgmTheme: (t: BgmTheme) => setTheme(t),
        playClick: () => settings.sound && playClickSfx(),
        playPopBalls: () => settings.sound && playPopBallsSfx(),
        playWinSmallPrize: () => settings.sound && playWinSmallPrizeSfx(),
        playPopLoss: () => settings.sound && playPopLossSfx(),
        playBingoWin: () => settings.sound && playBingoWinSfx(),
        playClaim: () => settings.sound && playClaimSfx(),
        // Legacy aliases
        playPop: () => settings.sound && playPopBallsSfx(),
        playFanfare: () => settings.sound && playBingoWinSfx(),
    };

    return (
        <AudioContext.Provider value={contextValue}>
            {children}
        </AudioContext.Provider>
    );
}
